import * as React from 'react';
import * as styles from './Image.css';
import "../../../styles/BasicIA.css";
import { Helper } from '../../../Helper';
import { Controller } from '../controller/Controller';
import { IComposerImage } from '../../../interfaces/IComposerImage';
import { IAImageCropper } from '../../imageCropper/ImageCropper';
import { ICropSetting } from '../../../interfaces/ICropSetting';
import { LocalizationService } from '../../../services/LocalizationService';
import { inject, observer } from 'mobx-react';
import { Store } from '../stores';
import { ComposerHelper } from '../helper/ComposerHelper';
import { IAComposerError } from '../placeholders/ComposerError';
import { IAComposerPlaceholder } from '../placeholders/ComposerPlaceholder';
import { Environment } from '../../../services/Environment';
import { ENTER_KEY, SPACEBAR_KEY } from '../../../global/Constants';

export interface IProps {
  store?: Store;
  image: IComposerImage;
  id: string;
  index?: number;
  hideToolbox: boolean;
  showDropZones: boolean;
  isInMobileToolbox?: boolean;
  isCover?: boolean;
  onFocus?: () => void;
  onChange: (image: IComposerImage) => void;
  onDrag?: () => void;
  onDragEnd?: () => void;
  onToggleCropper?: (showImageCropper: boolean) => void;
}

export interface IState {
  dragging: boolean;
  showController: boolean;
  showImageCropper: boolean;
  showDropZones: boolean;
  imageFormatError: boolean;
  imageUploadError: "too big" | "failed" | "";
  isLoadingImage: boolean;
  uploadProgressPercentage: number;
  videoUrlInImageUrlError: boolean;
  tryAgain: boolean;
}

@inject('store')
@observer
export class Image extends React.Component<IProps, IState> {

  private readonly localizationService: LocalizationService = new LocalizationService();

  constructor(props: IProps) {
    super(props);
    this.state = {
      dragging: false,
      showController: this.props.isInMobileToolbox ? true : false,
      showImageCropper: false,
      showDropZones: false,
      imageFormatError: false,
      imageUploadError: "",
      isLoadingImage: true,
      uploadProgressPercentage: undefined,
      videoUrlInImageUrlError: false,
      tryAgain: false,
    };
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
    document.body.addEventListener('onmousedown', (event: any) => {
      var exceptions = '#IA_image *, #IA_toolboxContent *, #IA_OrgAssetsUploader *, #IA_StockPhotos *';
      if (!event.target.matches(exceptions) && this.props.store.activeComponent && this.props.store.activeComponent.id === this.props.id) {
        this.hideToolbox()
        this.props.store.activeComponent = undefined;
      }
    });
  }

  public componentWillReceiveProps(props: IProps): void {
    if (props.hideToolbox) {
      this.hideToolbox();
    }
    if (props.showDropZones) {
      this.hideToolbox()
    }
    if (this.props.hideToolbox !== props.hideToolbox && props.hideToolbox != undefined && !props.hideToolbox) {
      this.setState({ showController: true });
    }
    if (props.showDropZones !== this.props.showDropZones) {
      setTimeout(() => {
        this.setState({ showDropZones: props.showDropZones });
      }, 0);
    }
    if (this.props.image?.url && !props.image?.url) {
      this.setState({ isLoadingImage: true });
    }
  }

  private showToolbox(): void {
    this.setState({ showController: true });
    this.props.store.linkEditorImageTarget = this.props.store.activeComponent?.image?.openInNewTab;
    this.forceUpdate();
  }

  private hideToolbox(): void {
    this.setState({ showController: false });
    this.forceUpdate();
  }

  private toggleCropper(showImageCropper: boolean): void {
    this.setState({ showImageCropper }, () => {
      if (this.props.onToggleCropper) this.props.onToggleCropper(showImageCropper);
    });
  }

  // saves all succesfull uploads on the message
  private setStateAndUpdateMessage(file: IComposerImage): void {
    if (file.status !== "success") {
      file.status === "too big" ? this.setState({ imageUploadError: "too big" }) : this.setState({ imageUploadError: "failed" });
    }
    //State update moved to OnLoad function on image itself. This is to ensure we do not try to display the image before it is loaded.
    this.props.onChange(file);
  }

  private getImageStatus(): "success" | "waiting" | "error" {
    if (!this.props.image.url && this.state.videoUrlInImageUrlError) {
      this.setState({ videoUrlInImageUrlError: false });
    }
    if (this.props.image.status === "failed" || this.props.image.status === "too big" || this.props.image.status === "duplicate" || this.state.imageFormatError || this.state.videoUrlInImageUrlError) {
      return "error";
    }
    if (!this.props.image.url || this.props.image.status === "waiting" || this.props.image.status === "uploading") {
      return "waiting";
    }
    return "success";
  }

  private getPlaceholderText(): string {
    if (this.props.image.source === "Upload" && this.props.store.activeComponent && this.props.store.activeComponent.id === this.props.id) {
      return this.localizationService.strings.Composer_DropAnImageHere;
    }
    return this.localizationService.strings.Composer_UploadImageOrPickOne;
  }

  private getErrorText(): string {
    if (this.state.imageFormatError) {
      return this.localizationService.strings.Composer_ImageFormatError;
    }
    if (this.state.imageUploadError === "too big") {
      return this.localizationService.strings.Composer_ImageUploadSizeError;
    }
    if (this.state.videoUrlInImageUrlError) {
      return this.localizationService.strings.Composer_VideoUrlInImageFieldError;
    }
    return this.localizationService.strings.Composer_ImageUploadGenericError;
  }

  private imageOnClick(): void {
    setTimeout(() => {
      this.props.onFocus();
      this.showToolbox();
    }, 100);
  }

  private onUploadProgress(progressPercentage: number) {
    this.setState({ uploadProgressPercentage: progressPercentage });
  }

  private finalizeUpload() {
    this.props.image.fileRef = null;
    this.setStateAndUpdateMessage(this.props.image);
    this.props.store.isUploadingVideoOrImage = false;
    this.setState({ uploadProgressPercentage: undefined });
  }


  public render(): JSX.Element {
    let aspect = 16 / 9;
    if (this.props.image && this.props.image.imageOrientation === "Vertical") {
      aspect = 3 / 4;
    }
    let height = this.props.image && this.props.image.imageOrientation === "Vertical" ? (this.props.store.isMobile ? (screen.width - 100) / 3 * 4 : 867) : (this.props.image.imageOrientation === "Custom" && this.props.image.url ? "auto" : (this.props.store.isMobile ? (screen.width - 100) / 16 * 9 : 348));
    if (this.state.showImageCropper) height = "auto";
    if (this.props.isCover) height = "100%";

    const imageStatus = this.getImageStatus();
    let showController = this.state.showController && !this.state.showDropZones;
    if (showController && this.props.isCover && !this.props.store.activeComponent?.image?.url) showController = false;

    return (
      <div
        id={"IA_image"}
        className={[styles.IA_image, "IA_OnFocus"].join(" ")}
        style={{
          clear: this.props.image && this.props.image.imageOrientation === "Custom" && this.props.image.url ? "none" : "both",
          marginRight: this.props.image && this.props.image.imageOrientation === "Custom" && this.props.image.url ? 20 : 0,
          marginBottom: this.props.isCover ? 0 : 20,
          height: this.state.showDropZones ? "auto" : height
        }}
        tabIndex={this.props.isCover ? -1 : 0}
        role={this.props.isCover ? undefined : "button"}
        onKeyDown={(event) => {
          if (!this.props.isCover && (event.key === ENTER_KEY || event.key === SPACEBAR_KEY)) {
            event.preventDefault();
            this.imageOnClick();
          }
        }}
      >
        {((this.props.store.isMobile && this.props.isInMobileToolbox) || !this.props.store.isMobile) &&
          <Controller
            visible={showController}
            highlightColor={this.props.store.highlightColor}
            onCrop={this.props.image && this.props.image.url && !this.props.image.url?.includes(".gif") && (this.props.image.imageOrientation !== "Custom" || this.props.isCover) ? () => this.toggleCropper(true) : undefined}
            onDublicate={this.props.isInMobileToolbox || this.props.isCover ? undefined : () => this.props.store.dublicateComponent()}
            onDelete={() => {
              if (this.props.store.activeComponent.image && this.props.store.activeComponent.image.url) {
                this.props.store.activeComponent.image = {
                  source: "Upload",
                  imageOrientation: "Custom",
                  width: 620
                };
              } else if (!this.props.isCover) {
                this.props.store.removeComponentAtIndex(this.props.index);
                this.hideToolbox()
                this.props.store.activeComponent = undefined;
              }
              this.forceUpdate();
            }}
            onDrag={this.props.onDrag ? () => this.props.onDrag() : undefined}
            onDragEnd={this.props.onDragEnd ? () => this.props.onDragEnd() : undefined}
          />
        }
        {this.state.showDropZones && this.props.index === 0 && !this.state.showController &&
          <div
            className={styles.IA_dropZones}
            style={{
              marginBottom: 10
            }}
            onDragOver={(event) => {
              event.preventDefault();
            }}
            onDragEnter={(event) => {
              event.preventDefault();
            }}
            onDropCapture={(event) => {
              event.preventDefault();
              if (this.props.store.startIndex != undefined) {
                this.props.store.moveComponentAtIndex(0);
              } else {
                this.props.store.addComponentAtIndex(0);
              }
            }}
          />
        }
        <div style={{ width: "100%", height: "100%", float: "left" }}>
          {imageStatus === "error" &&
            <IAComposerError
              errorMessage={this.getErrorText()}
              onClick={() => this.imageOnClick()}
              borderRadius={this.props.isCover ? 5 : this.props.image?.borderRadius}
            />
          }
          {imageStatus === "waiting" &&
            <IAComposerPlaceholder
              id={Helper.getRandomStringKey()}
              enableUpload={this.props.image.source === "Upload"}
              isActive={this.props.store.activeComponent && this.props.store.activeComponent.id === this.props.id}
              highlightColor={this.props.store.highlightColor}
              icon={{
                name: "Picture",
                size: 60
              }}
              showSpinner={false}
              progressBarPercentage={this.state.uploadProgressPercentage}
              progressBarText={this.localizationService.strings.Composer_Uploading}
              text={this.getPlaceholderText()}
              borderRadius={this.props.isCover ? 5 : this.props.image?.borderRadius}
              uploadButtonText={this.localizationService.strings.Composer_SelectFromMediaLibrary}
              uploadFileCallback={async (file) => {
                this.props.store.isUploadingVideoOrImage = true;
                let site: string;
                let serverRelativeUrl: string;
                if (this.props.image.organizationalAssetPath) {
                  site = this.props.image.organizationalAssetPath.split("/")[2];
                  serverRelativeUrl = this.props.image.organizationalAssetPath.split(`/${site}/`)[1];
                }
                ComposerHelper.uploadFileDirectlyToSharepoint(this.props.store.environment, file, this.props.store.tenant, this.props.store.token, (progress) => this.onUploadProgress(progress), site, serverRelativeUrl)
                  .then(fileUrl => {
                    setTimeout(() => {
                      // wait for progress bar animation to finish
                      this.setState({ uploadProgressPercentage: undefined });
                      this.props.image.status = "success";
                      this.props.image.url = fileUrl;
                      this.finalizeUpload();
                    }, 500);
                  }).catch(() => {
                    this.props.image.status = "failed";
                    this.finalizeUpload();
                  });
              }}
              onClick={() => this.imageOnClick()}
            />
          }
          {imageStatus === "success" &&
            <>
              {this.state.showImageCropper ?
                <IAImageCropper
                  token={this.props.store.token}
                  tenant={this.props.store.tenant}
                  environment={this.props.store.environment}
                  image={this.props.image.url}
                  showButtons={true}
                  enableZoom={true}
                  aspect={aspect}
                  initialCrop={this.props.image?.cropSettings}
                  highlightColor={this.props.store.highlightColor}
                  height={400}
                  onUpdate={(crop: ICropSetting) => {
                    console.log(crop);
                  }}
                  onSave={(crop: ICropSetting) => {
                    let image = this.props.image;
                    if (image) {
                      image.cropSettings = crop;
                      image.modified = new Date().toString().replace(/[^\d]/g, '');
                      this.props.onChange(image);
                    }
                    this.setState({ isLoadingImage: true }, () => this.toggleCropper(false));
                  }}
                  onCancel={() => {
                    this.toggleCropper(false);
                  }}
                  onClick={() => this.imageOnClick()}
                  onReset={() => {
                    let image = this.props.image;
                    if (image) {
                      image.cropSettings = null;
                      image.modified = new Date().toString().replace(/[^\d]/g, '');
                      this.props.onChange(image);
                    }
                    this.toggleCropper(false);
                  }}
                />
                :
                <div style={{ width: "100%", height: this.state.isLoadingImage && !this.props.store.isMobile ? 350 : height }}>
                  <img
                    id={`ComposerImage_${this.props.id}`}
                    style={{
                      objectFit: "cover",
                      width: this.props.image.imageOrientation === "Custom" ? ComposerHelper.getCustomWidth(this.props.image.width) : "100%",
                      opacity: this.state.isLoadingImage ? 0 : 1,
                      height: this.state.isLoadingImage && !this.props.store.isMobile ? 350 : height,
                      position: this.state.isLoadingImage ? "absolute" : "static",
                      cursor: "pointer",
                      borderRadius: this.props.isCover ? 5 : this.props.image?.borderRadius
                    }}
                    src={this.props.store.tenantSettings?.admin?.useBlobImages && this.props.image.imageBlobUrl ? this.props.image.imageBlobUrl : Helper.getImageUrl(Environment.getEnvironmentForImageAPI(this.props.store.environment), this.props.store.tenant, this.props.store.token, this.props.image.url, this.props.image.modified, this.state.tryAgain ? "StandardSmall" : "Full", this.props.image?.cropSettings)}
                    onLoad={() => this.setState({ isLoadingImage: false })}
                    onClick={() => this.imageOnClick()}
                    onError={() => {
                      if (this.state.tryAgain) {
                        this.setState({ videoUrlInImageUrlError: true, isLoadingImage: false })
                      } else {
                        this.setState({ tryAgain: true });
                      }
                    }}
                  />
                  {this.state.isLoadingImage &&
                    <IAComposerPlaceholder
                      id={Helper.getRandomStringKey()}
                      highlightColor={this.props.store.highlightColor}
                      isActive={this.props.store.activeComponent && this.props.store.activeComponent.id === this.props.id}
                      showSpinner={true}
                      borderRadius={this.props.isCover ? 5 : this.props.image?.borderRadius}
                      onClick={() => this.imageOnClick()}
                    />
                  }
                </div>
              }
            </>
          }
        </div>

        {this.state.showDropZones && !this.state.showController &&
          <div
            className={styles.IA_dropZones}
            style={{
              marginTop: 10
            }}
            onDragOver={(event) => {
              event.preventDefault();
            }}
            onDragEnter={(event) => {
              event.preventDefault();
            }}
            onDropCapture={(event) => {
              event.preventDefault();
              event.preventDefault();
              if (this.props.store.startIndex != undefined) {
                this.props.store.moveComponentAtIndex(this.props.index + 1);
              } else {
                this.props.store.addComponentAtIndex(this.props.index + 1);
              }
            }}
          />
        }
      </div>
    );
  }
}